import {
	Box,
	Checkbox,
	Radio,
	RadioGroup,
	Stack,
	Text,
	useToast,
} from '@chakra-ui/react';
import { IconTrash } from '@tabler/icons';
import GenericModal from 'components/modal/genericModal/GenericModal';
import { hierarchyMessages } from 'pages/admin/cadastros/comercial/hierarquia/variables/hierarchyMessages';
import { useEffect, useState } from 'react';
import { useGetRelatedRecordsCount } from 'services/queryClient/wrapperHooks/hierarchy/useGetRelatedRecordsCount';

interface ModalDeleteHierarchyProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (
		action: 'realocar' | 'excluir',
		typesToDelete?: string[]
	) => void;
	hierarquiaId: number | null;
	isLoading?: boolean;
}

interface RecordType {
	key: string;
	label: string;
	count: number;
}

export const ModalDeleteHierarchy = ({
	isOpen,
	onClose,
	onConfirm,
	hierarquiaId,
	isLoading = false,
}: ModalDeleteHierarchyProps) => {
	const [action, setAction] = useState<'realocar' | 'excluir' | ''>('');
	const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
	const toast = useToast();

	const { data: count, isLoading: isLoadingCount } =
		useGetRelatedRecordsCount(hierarquiaId, isOpen);

	useEffect(() => {
		if (!isOpen) {
			setAction('');
			setSelectedTypes([]);
		}
	}, [isOpen]);

	const recordTypes: RecordType[] = count
		? [
				{
					key: 'vendas',
					label: 'Vendas',
					count: count.vendas,
				},
				{
					key: 'carteira_pedidos',
					label: 'Carteira de Pedidos',
					count: count.carteira_pedidos,
				},
				{
					key: 'oportunidades',
					label: 'Oportunidades',
					count: count.oportunidades,
				},
				{
					key: 'orcamentos',
					label: 'Orçamentos',
					count: count.orcamentos,
				},
				{
					key: 'planos_irrestritos',
					label: 'Planos Irrestritos',
					count: count.planos_irrestritos,
				},
				{
					key: 'planos_restritos',
					label: 'Planos Restritos',
					count: count.planos_restritos,
				},
				{
					key: 'planos_colaboracao_comercial',
					label: 'Planos da Colaboração Comercial',
					count: count.planos_colaboracao_comercial,
				},
				{
					key: 'planos_colaboracao_marketing',
					label: 'Planos da Colaboração de Marketing',
					count: count.planos_colaboracao_marketing,
				},
				{
					key: 'planos_colaboracao_planejamento',
					label: 'Planos da Colaboração de Planejamento',
					count: count.planos_colaboracao_planejamento,
				},
		  ].filter(type => type.count > 0)
		: [];

	const handleToggleType = (key: string) => {
		setSelectedTypes(prev =>
			prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
		);
	};

	const handleSelectAll = () => {
		if (selectedTypes.length === recordTypes.length) {
			setSelectedTypes([]);
		} else {
			setSelectedTypes(recordTypes.map(t => t.key));
		}
	};

	const hasRecords = count && count.total > 0;

	const handleConfirm = () => {
		if (!hasRecords) {
			onConfirm('excluir', []);
			return;
		}

		if (!action) {
			toast({
				title: hierarchyMessages.TOASTS.REQUIRED_OPTION_TITLE,
				description:
					hierarchyMessages.TOASTS.REQUIRED_OPTION_DESCRIPTION,
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-left',
			});
			return;
		}

		if (action === 'excluir' && selectedTypes.length === 0) {
			toast({
				title: hierarchyMessages.TOASTS.REQUIRED_OPTION_TITLE,
				description: hierarchyMessages.TOASTS.REQUIRED_TYPE_DESCRIPTION,
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-left',
			});
			return;
		}

		onConfirm(action, action === 'excluir' ? selectedTypes : undefined);
	};

	const handleCancel = () => {
		toast({
			title: hierarchyMessages.TOASTS.OPERATION_CANCELLED,
			status: 'info',
			duration: 3000,
			isClosable: true,
			position: 'bottom-left',
		});
		onClose();
	};

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={onClose}
			onCancel={handleCancel}
			onSave={handleConfirm}
			title={hierarchyMessages.MODAL_DELETE.TITLE}
			titleIcon={IconTrash}
			titleIconColor='easyRED.300'
			saveButtonText={
				hasRecords
					? hierarchyMessages.BUTTONS.CONFIRM_DELETE_REALLOCATE
					: hierarchyMessages.BUTTONS.CONFIRM_DELETE
			}
			cancelButtonText={hierarchyMessages.BUTTONS.CANCEL}
			maxWidth='600px'
			isSaving={isLoading || isLoadingCount}
			disableSaveButton={isLoading || isLoadingCount}
		>
			<Box mt={-6}>
				{isLoadingCount ? (
					<Text color='gray.500'>
						{hierarchyMessages.MODAL_DELETE.LOADING}
					</Text>
				) : hasRecords ? (
					<>
						<Text
							color='gray.500'
							fontSize='sm'
							fontWeight={200}
							mb={4}
						>
							{hierarchyMessages.MODAL_DELETE.HAS_RECORDS}
						</Text>
						<RadioGroup
							value={action}
							onChange={value =>
								setAction(value as 'realocar' | 'excluir')
							}
							mb={4}
						>
							<Stack spacing={4}>
								<Radio value='realocar'>
									<Text fontSize='sm'>
										{
											hierarchyMessages.MODAL_DELETE
												.OPTION_REALLOCATE
										}
									</Text>
								</Radio>
								<Radio value='excluir'>
									<Text fontSize='sm'>
										{
											hierarchyMessages.MODAL_DELETE
												.OPTION_DELETE
										}
									</Text>
								</Radio>
							</Stack>
						</RadioGroup>

						{action === 'excluir' && (
							<Box
								mt={4}
								p={4}
								bg='gray.50'
								borderRadius='md'
							>
								<Text
									fontSize='sm'
									fontWeight='bold'
									mb={3}
								>
									{
										hierarchyMessages.MODAL_DELETE
											.SELECT_TYPES_TITLE
									}
								</Text>

								<Stack spacing={2}>
									<Checkbox
										isChecked={
											selectedTypes.length ===
												recordTypes.length &&
											recordTypes.length > 0
										}
										onChange={handleSelectAll}
									>
										<Text
											fontSize='sm'
											fontWeight='bold'
										>
											{
												hierarchyMessages.MODAL_DELETE
													.SELECT_ALL
											}
										</Text>
									</Checkbox>

									{recordTypes.map(type => (
										<Checkbox
											key={type.key}
											isChecked={selectedTypes.includes(
												type.key
											)}
											onChange={() =>
												handleToggleType(type.key)
											}
										>
											<Text fontSize='sm'>
												{type.label} ({type.count}{' '}
												{
													hierarchyMessages
														.MODAL_DELETE
														.RECORD_COUNT
												}
												)
											</Text>
										</Checkbox>
									))}
								</Stack>

								<Text
									fontSize='xs'
									color='gray.500'
									mt={3}
									fontStyle='italic'
								>
									{
										hierarchyMessages.MODAL_DELETE
											.VOLUME_WARNING
									}
								</Text>
							</Box>
						)}
					</>
				) : (
					<Text
						color='gray.500'
						fontSize='sm'
						fontWeight={200}
					>
						{hierarchyMessages.MODAL_DELETE.NO_RECORDS}
					</Text>
				)}
			</Box>
		</GenericModal>
	);
};
